import React from 'react';
import { useSearchParams } from 'react-router-dom';
import PageLayout from '../components/global/PageLayout';
import useUser from '../hooks/global/useUser';
import ColoredAlert from '../components/global/ColoredAlert';
import OnboardingBanner from '../components/onboarding/OnboardingBanner';
import CenteredContentLayout from '../components/global/CenteredContentLayout';
import PageLoading from '../components/global/PageLoading';
import {
  getBnplDisabledReason,
  getBnplNeedsStripeDashboard,
} from '../helpers/payments';
import useStripeAccountActions from '../hooks/stripe/useStripeAccountActions';
import useStripeSummary from '../hooks/stripe/useStripeSummary';
import StripeAccountSummary from '../components/stripe/StripeAccountSummary';
import { canManageStripeAccount } from '../helpers/businesses';
import TabBar from '../components/filtering/TabBar';
import { TabInt } from '../interfaces';
import StripeBankPayouts from '../components/stripe/StripeBankPayouts';

export enum PaymentsTab {
  ACCOUNT_DETAILS = 'account-details',
  BANK_PAYOUTS = 'bank-payouts',
}

export default function PaymentsPage() {
  const { user } = useUser();

  const {
    data,
    isFetching,
    refetch,
    isLoading: isLoadingStripe,
  } = useStripeSummary();
  const { isLinking, isGettingLogin, onStartSetup, onViewStripeAccount } =
    useStripeAccountActions();
  const {
    status: {
      isOnboardComplete = false,
      isPendingReview = false,
      isDisabled = false,
      isIncomplete = false,
    } = {},
    capabilities,
  } = data || {};
  const isLoading = isLoadingStripe;

  const isAffirmDisabled = capabilities && !capabilities.affirm.active;
  const isAfterpayDisabled = capabilities && !capabilities.afterpay.active;

  const { title, description, buttonText, buttonAction } = React.useMemo(() => {
    switch (true) {
      case isIncomplete:
        return {
          title: 'Finish Connecting Your Account',
          description:
            'Your shop account with Stripe is not yet set up! Follow the link below to finish setting up your stripe account so you can receive payouts from Porter.',
          buttonText: 'Continue',
          buttonAction: onStartSetup,
        };
      case isDisabled:
        return {
          title: 'Your account is disabled',
          description:
            'Your shop account has been disabled. Please check your account or reach out to our support team for assistance.',
          buttonText: 'View Stripe Account',
          buttonAction: onViewStripeAccount,
        };
      case isPendingReview:
        return {
          title: 'Your account is pending review',
          description:
            'Your shop account is pending review. Please check back later to see if your account has been approved. For any questions or assistance, reach out to our support team.',
          buttonText: 'View Stripe Account',
          buttonAction: onViewStripeAccount,
        };
      case isOnboardComplete:
        return {
          title: 'Your account is connected!',
          description:
            'Your shop account is successfully connected through Stripe. Payouts will typically hit your bank account 2-4 business days after payment is made by the client. For any questions or assistance, reach out to our support team.',
          buttonText: 'View Stripe Account',
          buttonAction: onViewStripeAccount,
        };
      default:
        return {
          title: 'Set up your shop payments account',
          description:
            "Set up the payments account to transfer Shop funds to below. We use Stripe to make sure you get paid on time and to keep your bank details secured. Click Set up Payments below to set up your payments & connect your shop's bank account through Stripe.",
          buttonText: 'Set Up Payments',
          buttonAction: onStartSetup,
        };
    }
  }, [
    isDisabled,
    isIncomplete,
    isOnboardComplete,
    isPendingReview,
    onStartSetup,
    onViewStripeAccount,
  ]);

  const [params, setParams] = useSearchParams();
  const tabs: TabInt[] = React.useMemo(
    () =>
      !data
        ? []
        : [
            {
              id: PaymentsTab.ACCOUNT_DETAILS,
              label: 'Account Details',
              component: <StripeAccountSummary summary={data} />,
            },
            {
              id: PaymentsTab.BANK_PAYOUTS,
              label: 'Bank Payouts',
              component: <StripeBankPayouts summary={data} />,
            },
          ],
    [data],
  );

  const paramTab = params.get('tab') as PaymentsTab | null;
  const selectedTab = paramTab
    ? tabs.find(tab => tab.id === paramTab)?.id || tabs[0]?.id
    : tabs[0]?.id;

  const selectedTabIndex = Math.max(
    tabs.findIndex(tab => tab.id === selectedTab),
    0,
  );

  const setSelectedTabIndex = (index: number) => {
    const newTab = tabs[index]?.id || tabs[0]?.id;
    const newParams = new URLSearchParams(params);
    if (newTab) {
      newParams.set('tab', newTab as string);
    }
    setParams(newParams);
  };

  const isStripeAccountSetup =
    isOnboardComplete || !!user?.business?.connectedStripeAt;

  return (
    <PageLayout
      topSection={<OnboardingBanner step="PAYMENTS" />}
      title="Payments Account"
      isLoading={isFetching && !isLoading}
      refresh={() => {
        refetch();
      }}
      guard={{
        isAllowed: canManageStripeAccount(user),
      }}>
      <ColoredAlert
        animate={false}
        title="This is for the shops bank account!"
        subtitle="The payments account you set up below is where any money owed to the shop will be sent. If you personally tattoo, and need to set up a separate bank account for your own payments, you can do so in the Porter mobile app (available on iPhone and Android)"
        type="warning"
        dismissible
        persistentDismissAlertKey="payments-account-dismissed"
      />
      {isStripeAccountSetup && (isAffirmDisabled || isAfterpayDisabled) && (
        <ColoredAlert
          animate={false}
          title="Some BNPL services are not available"
          subtitle={getBnplDisabledReason(capabilities).join('\n')}
          type="warning"
          button={
            getBnplNeedsStripeDashboard(capabilities)
              ? {
                  text: 'View Stripe Dashboard',
                  onClick: onViewStripeAccount,
                }
              : undefined
          }
        />
      )}
      {isLoading ? (
        <PageLoading />
      ) : !data?.stripeAccountId || !isStripeAccountSetup ? (
        <CenteredContentLayout
          image={{
            src: '/money-tree.gif',
            alt: 'payments',
          }}
          title={title}
          description={description}
          button={{
            text: buttonText,
            onClick: buttonAction,
            loading: isLinking || isGettingLogin,
          }}
        />
      ) : (
        <TabBar
          tabs={tabs}
          currentTab={selectedTabIndex}
          changeTab={setSelectedTabIndex}
        />
      )}
    </PageLayout>
  );
}
